import queryString from 'query-string';

const { getBlockingCollectionNamesForRouteContext, getRouteContextFromFragment, shouldWaitForDashboardEvents } = require('@/bootstrap/route-config');

const bootstrapCollectionNames = [
    'actuators',
    'analyzeViews',
    'apiRequests',
    'siteTags',
    'sitePackages',
    'circuitTags',
    'companies',
    'zone_attribute_values',
    'zone_attributes',
    'alarms',
    'allocations',
    'devices',
    'etls',
    'groups',
    'holidays',
    'invoices',
    'markers',
    'objectives',
    'opportunities',
    'organizationLogs',
    'reports',
    'siteDocuments',
    'scheduledReports',
    'tariffs',
    'tars',
    'tenants',
    'tenantInvoices'
];

const adminCollectionNames = [
    'integrations',
    'integrationLicenses',
    'integrationLicenseLogs',
    'integrationConfigurations'
];

const superUserCollectionNames = ['leads'];

const getCollectionsByName = names => {
    return [...new Set(names)]
        .map(name => WiseMetering[name])
        .filter(Boolean);
};

const getRootObjectBlockingCollectionNames = (routeContext) => {
    if (routeContext.routeName !== 'drawRootObject' || !routeContext.parts[1]) {
        return [];
    }

    const rootObject = WiseMetering.rootObjects.get(routeContext.parts[1]);

    if (rootObject?.get('slug') === 'tenants') {
        return ['companies', 'tenants'];
    }

    return [];
};

export const fetchCollections = collections => {
    return Promise.all([...new Set(collections.filter(Boolean))].map(collection => Promise.resolve(collection.fetch())));
};

export const scheduleBackgroundWork = callback => {
    const run = () => void callback();

    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 1000 });
    } else {
        window.setTimeout(run, 0);
    }
};

export const loadBrowserLang = () => {
    let browserLang = (navigator.languages && navigator.languages[0]) || navigator.language || 'en';

    browserLang = browserLang.split('-')[0];

    const supportedLangs = ['en', 'es', 'pt'];
    const langToLoad = supportedLangs.includes(browserLang) ? browserLang : 'en';

    WiseMetering.loadLang(langToLoad);
};

export const applyBootstrapData = bootstrapData => {
    WiseMetering.currentLocale = WiseMetering.locales[WiseMetering.currentOrganization.get('country_code')] || WiseMetering.locales['PT'];
    WiseMetering.number_formatter = new NumberFormatter(WiseMetering.currentLocale);
    WiseMetering.actuatorKinds = new WiseMetering.Collection.ActuatorKinds(bootstrapData.actuator_kinds);
    WiseMetering.alarmTypes = bootstrapData.alarm_types;
    WiseMetering.countries = new WiseMetering.Collection(bootstrapData.countries);
    WiseMetering.currencies = bootstrapData.currencies;
    WiseMetering.etlDrivers = bootstrapData.etl_drivers;
    WiseMetering.etlRates = bootstrapData.etl_rates;
    WiseMetering.globalReportTypes = new WiseMetering.Collection.GlobalReportTypes(bootstrapData.organization_report_types);
    WiseMetering.indicatorKinds = new WiseMetering.Collection(bootstrapData.indicator_kinds);
    WiseMetering.integrationPartners = new WiseMetering.Collection.IntegrationPartners(bootstrapData.integration_partners);
    WiseMetering.markerTypes = new WiseMetering.Collection(bootstrapData.marker_types);
    WiseMetering.metadataTypes = bootstrapData.metadata_types;
    WiseMetering.packages = new WiseMetering.Collection.Packages(bootstrapData.packages);
    WiseMetering.rootObjects = new WiseMetering.Collection.RootObjects().build();
    WiseMetering.systemCategories = new WiseMetering.Collection(bootstrapData.system_categories);
    WiseMetering.tariffTypes = new WiseMetering.Collection.TariffTypes(bootstrapData.tariff_types);
    WiseMetering.temperatureColors = bootstrapData.temperature_colors;
    WiseMetering.tenantIntervals = bootstrapData.tenant_intervals;
    WiseMetering.timezones = bootstrapData.timezones;
    WiseMetering.units = new WiseMetering.Collection(bootstrapData.units);
    WiseMetering.utilityKinds = new WiseMetering.Collection.UtilityKinds(bootstrapData.utility_kinds);
    WiseMetering.zoneCategories = new WiseMetering.Collection(bootstrapData.zone_categories);
    WiseMetering.zoneReportTypes = new WiseMetering.Collection.ZoneReportTypes(bootstrapData.site_report_types);
    WiseMetering.organizationTypes = bootstrapData.organization_types.map(slug => ({
        name: i18next.t(`organizations.type.${slug}`),
        slug
    }));
    WiseMetering.workflowActions = {
        pending: 'reset',
        ongoing: 'start',
        completed: 'complete',
        active: 'activate',
        discarded: 'discard'
    };
};

export const showSitesTree = () => {
    WiseMetering.layout.tree.show(new WiseMetering.Views.Sites({
        // Use a plain Backbone collection to preserve the visible tree order.
        collection: new Backbone.Collection([
            ...WiseMetering.folders.roots().models,
            ...WiseMetering.zones.activeSites().models
        ])
    }));
};

export const getBootstrapCollections = () => {
    const collectionNames = [...bootstrapCollectionNames];

    if (WiseMetering.currentUser.isAdministrator()) {
        collectionNames.push(...adminCollectionNames);
    }

    if (WiseMetering.currentUser.isSuperUser()) {
        collectionNames.push(...superUserCollectionNames);
    }

    return getCollectionsByName(collectionNames);
};

export const getFetchPlan = collections => {
    const routeContext = getRouteContextFromFragment(Backbone.history.getFragment() || 'home');
    const blockingCollectionNames = [
        ...getBlockingCollectionNamesForRouteContext(routeContext),
        ...getRootObjectBlockingCollectionNames(routeContext)
    ];
    const blockingCollections = new Set(getCollectionsByName(blockingCollectionNames));

    return {
        backgroundCollections: collections.filter(collection => !blockingCollections.has(collection)),
        initialCollections: collections.filter(collection => blockingCollections.has(collection)),
        waitForDashboardEvents: shouldWaitForDashboardEvents(routeContext)
    };
};

export const loadUrlParams = () => {
    const params = queryString.parse(location.search);

    if (params.from) {
        ParamsService.setParams({ from: params.from });
    }

    if (params.rate) {
        ParamsService.setParams({ rate: params.rate });
    }

    if (params.to) {
        ParamsService.setParams({ to: params.to });
    }
};
